"use client";

/**
 * CtaStepSequenceDialog
 * The one dialog a multi-step CTA runs in. It stays open across steps and swaps its content: a
 * loading or error state, the returning guest's steps overview, or one step (a FormModal or
 * HotelBookingModal rendered `embedded`). A step opened from the overview gets "‹ All steps" above
 * its title and previous/next links; leaving a step with unsaved changes asks first, inline. The
 * dialog is a small card, widening only while a step's form is open for input.
 *
 * State lives in `useCtaStepSequence`; fetching and starting live in `useLinkedStepsCta`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import FetchModal from "@/fetch-ui/FetchModal";
import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchCallout } from "@/fetch-ui/FetchCallout";
import { useToast } from "@/components/providers/FetchToastProvider";
import {
  cancelOwnHotelBookingAction,
  restoreOwnHotelBookingAction,
} from "@/features/hotel-booking/_logic/hotel-booking-guest-actions";
import {
  type CtaHotelStepOverview,
  type CtaStep,
  type CtaStepSequenceContext,
  type CtaStepsOverview,
  type CombinedFormHandoff,
  EMPTY_CTA_STEP_CONTACT,
} from "../../_logic/cta-steps";
import { describeOverviewStep, hasDeclinedStep } from "../../_logic/cta-steps-overview-display";
import type { CombinedFormHotelConfig } from "../../_logic/constants/combined-form-hotel";
import {
  getCachedHotelBookingEmbedBundle,
  invalidateHotelBookingEmbedBundleCache,
} from "../../_logic/hotel-booking-embed-bundle-cache";
import type { useCtaStepSequence } from "../../_hooks/useCtaStepSequence";
import { useUnsavedStepGuard } from "../../_hooks/useUnsavedStepGuard";
import { CtaStepsOverviewPanel, type OverviewRoomActions } from "./CtaStepsOverviewPanel";
import { FormModal } from "./FormModal";
import { LazyHotelBookingModal } from "./LazyHotelBookingModal";
import { PUBLIC_MODAL_COMPACT_CLASSNAME, PUBLIC_MODAL_FULL_SCREEN_MOBILE_CLASSNAME } from "./public-modal-classnames";

/** The sequence runner's state and actions. */
type SequenceRunner = ReturnType<typeof useCtaStepSequence>;

/** The overview data and its reload, owned by `useLinkedStepsCta`. */
export interface CtaOverviewSource {
  data: CtaStepsOverview | null;
  /** Re-fetches the overview (after a change). */
  refresh: () => Promise<void>;
  /** Retries the initial load after an error. */
  retry: () => void;
}

interface CtaStepSequenceDialogProps {
  steps: CtaStep[];
  runner: SequenceRunner;
  overview: CtaOverviewSource;
  display: { title: string; eventId: number; primaryColor: string };
  /** Set when the steps run as one combined form (see `resolveCombinedConfig`). */
  combined: CombinedFormHotelConfig | null;
}

/** "All steps" link above the title; py-3 keeps the tap target at 44px, -my-3 keeps it compact. */
const BACK_LINK_CLASS =
  "-my-3 inline-flex self-start touch-manipulation items-center gap-1 rounded py-3 pr-2 text-sm font-medium text-gray-600 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500";

/** Fallback step name before the overview has loaded (or for an anonymous guest). */
function fallbackName(step: CtaStep | undefined): string {
  return step?.kind === "hotel-booking" ? "Hotel booking" : "Form";
}

/**
 * Renders the multi-step CTA dialog, or nothing when the sequence isn't running.
 * @param props - See {@link CtaStepSequenceDialogProps}
 * @returns The dialog, or null
 * @throws Never
 */
export function CtaStepSequenceDialog({
  steps,
  runner,
  overview,
  display,
  combined,
}: CtaStepSequenceDialogProps): JSX.Element | null {
  // Hooks
  const { showToast } = useToast();
  const { state, complete: completeStep, close, openOverview, goTo, backTo, startRun } = runner;
  const viewKey = state ? (state.view === "step" ? `step-${state.index}` : state.view) : "closed";
  const guard = useUnsavedStepGuard(viewKey);

  // States
  const [pendingBookingId, setPendingBookingId] = useState<number | null>(null);
  // Bookings the guest chose to keep after declining. Lives here, not in the overview panel, so the
  // choice holds across steps and reopening the dialog while the page is open.
  const [keptBookingIds, setKeptBookingIds] = useState<ReadonlySet<number>>(() => new Set());
  // The view before the current one, to spot an arrival on the overview from a step.
  const previousViewRef = useRef<string | null>(null);
  // Combined run: the form's pending save, handed over by its last "Next" and run by the booking's
  // Submit. `isSaved` keeps a retried booking from saving the form twice.
  const handoffRef = useRef<{ submit: () => Promise<boolean>; isSaved: boolean } | null>(null);
  // Combined run: the handed-over form's page count (the booking shows as the page after) and name.
  const [handedOverForm, setHandedOverForm] = useState<{ pageCount: number; formName: string } | null>(null);

  // Derived values
  const stepIndex = state?.view === "step" ? state.index : null;
  const step = stepIndex === null ? undefined : steps[stepIndex];
  const stepOverview = stepIndex === null ? undefined : overview.data?.steps[stepIndex];
  const formOverview = stepOverview?.kind === "form" ? stepOverview : undefined;
  // A combined run renders the form and its booking as one form (see the step render below).
  const isCombinedRun = !!combined && state?.view === "step" && state.origin === "run";
  const hotelIndex = combined
    ? steps.findIndex((s) => s.kind === "hotel-booking" && s.bookingLinkId === combined.bookingLinkId)
    : -1;
  const hotelStep = hotelIndex === -1 ? undefined : steps[hotelIndex];
  const nameOf = useCallback(
    (index: number) => overview.data?.steps[index]?.name ?? fallbackName(steps[index]),
    [overview.data, steps]
  );
  const openableIndexes = useMemo(() => {
    if (!overview.data) return [];
    const hasDeclined = hasDeclinedStep(overview.data);
    return overview.data.steps.flatMap((s, i) => (describeOverviewStep(s, hasDeclined).openable ? [i] : []));
  }, [overview.data]);

  // Handlers
  /** The form's last "Next" in a combined run: move on to the booking, holding the form's save. */
  const handleDeferSubmit = useCallback(
    (handoff: CombinedFormHandoff) => {
      handoffRef.current = { submit: handoff.submit, isSaved: false };
      setHandedOverForm({ pageCount: handoff.pageCount, formName: handoff.formName });
      completeStep(0, { contact: handoff.contact, endedEarly: false });
    },
    [completeStep]
  );

  /** Runs the held form save before the booking; true once it's saved (or already was). */
  const submitDeferredForm = useCallback(async () => {
    const handoff = handoffRef.current;
    if (!handoff || handoff.isSaved) return true;
    handoff.isSaved = await handoff.submit();
    return handoff.isSaved;
  }, []);

  const handleBackToForm = useCallback(() => backTo(0), [backTo]);

  /** The context one step runs with; a combined run also wires the form's handoff to its booking. */
  const sequenceFor = useCallback(
    (index: number): CtaStepSequenceContext | null => {
      if (state?.view !== "step") return null;
      const { contact, origin } = state;
      const fromOverview = origin === "overview";
      return {
        origin,
        stepNumber: index + 1,
        stepCount: steps.length,
        // From the overview, completing a step goes back to the overview, never on to the next step.
        hasNextStep: !fromOverview && index + 1 < steps.length,
        // A combined run's form stays mounted and reloads its fields when this changes, so it keeps
        // the contact it started with instead of the one it just handed forward.
        prefillContact: isCombinedRun && index === 0 ? EMPTY_CTA_STEP_CONTACT : contact,
        onStepComplete: (completion) => completeStep(index, completion),
        recap: fromOverview ? (formOverview?.recap ?? null) : null,
        editTarget: fromOverview ? (formOverview?.editTarget ?? null) : null,
        allowsResubmit: formOverview?.allowsResubmit ?? false,
        // In a combined run both steps stay mounted; only the one on screen reports, and it reports
        // again whenever it becomes the one on screen (the callback appearing reruns its effect).
        onEditingChange: isCombinedRun && index !== stepIndex ? undefined : guard.setIsEditing,
        combined,
        deferSubmit: isCombinedRun && index === 0 ? handleDeferSubmit : undefined,
        deferredForm:
          isCombinedRun && index === hotelIndex && handedOverForm
            ? { ...handedOverForm, submit: submitDeferredForm, back: handleBackToForm }
            : undefined,
      };
    },
    [
      state,
      stepIndex,
      steps.length,
      completeStep,
      formOverview,
      guard.setIsEditing,
      combined,
      isCombinedRun,
      hotelIndex,
      handedOverForm,
      handleDeferSubmit,
      submitDeferredForm,
      handleBackToForm,
    ]
  );
  const sequence = useMemo(() => (stepIndex === null ? null : sequenceFor(stepIndex)), [stepIndex, sequenceFor]);
  // A combined run keeps the form and its booking mounted, so Back returns to every answer intact.
  const formRunSequence = useMemo(() => (isCombinedRun ? sequenceFor(0) : null), [isCombinedRun, sequenceFor]);
  const hotelRunSequence = useMemo(
    () => (isCombinedRun && hotelIndex !== -1 ? sequenceFor(hotelIndex) : null),
    [isCombinedRun, hotelIndex, sequenceFor]
  );

  const { guardLeave } = guard;
  // A combined run closes like a plain form popup: its form keeps a draft, so there's nothing to confirm.
  const handleClose = useCallback(
    () => (isCombinedRun ? close() : guardLeave(close)),
    [isCombinedRun, guardLeave, close]
  );
  const handleBackToOverview = useCallback(() => guardLeave(() => openOverview()), [guardLeave, openOverview]);
  const handleGoTo = useCallback((index: number) => guardLeave(() => goTo(index)), [guardLeave, goTo]);
  const handleStartRun = useCallback(() => startRun(), [startRun]);

  /** After a guest changes a booking, drop the cached embed bundle so the step reloads fresh. */
  const afterBookingChange = useCallback(
    async (target: CtaHotelStepOverview) => {
      invalidateHotelBookingEmbedBundleCache(display.eventId, target.bookingLinkId);
      await overview.refresh();
    },
    [display.eventId, overview]
  );

  const handleRestoreRoom = useCallback(
    async (target: CtaHotelStepOverview) => {
      if (target.responseId == null) return;
      setPendingBookingId(target.responseId);
      const result = await restoreOwnHotelBookingAction(target.responseId);
      setPendingBookingId(null);
      if (!result.success) {
        showToast(result.error, "error");
        return;
      }
      showToast("Your room is restored", "success");
      await afterBookingChange(target);
    },
    [afterBookingChange, showToast]
  );

  const handleReleaseRoom = useCallback(
    async (target: CtaHotelStepOverview) => {
      if (target.responseId == null) return;
      setPendingBookingId(target.responseId);
      const result = await cancelOwnHotelBookingAction(target.responseId);
      setPendingBookingId(null);
      if (!result.success) {
        showToast(result.error, "error");
        return;
      }
      showToast("Room released", "success", {
        action: { label: "Undo", onClick: () => void handleRestoreRoom(target) },
      });
      await afterBookingChange(target);
    },
    [afterBookingChange, handleRestoreRoom, showToast]
  );

  const handleKeepRoom = useCallback((target: CtaHotelStepOverview) => {
    const responseId = target.responseId;
    if (responseId != null) setKeptBookingIds((kept) => new Set(kept).add(responseId));
  }, []);

  const roomActions = useMemo<OverviewRoomActions>(
    () => ({
      pendingBookingId,
      keptBookingIds,
      onKeep: handleKeepRoom,
      onRelease: (target) => void handleReleaseRoom(target),
      onRestore: (target) => void handleRestoreRoom(target),
    }),
    [pendingBookingId, keptBookingIds, handleKeepRoom, handleReleaseRoom, handleRestoreRoom]
  );

  // Effects
  // Back on the overview from a step: confirm the save and refresh the statuses. Keyed on the
  // arrival (the previous view was a step), so the first load and later re-renders don't refetch.
  useEffect(() => {
    const previousView = previousViewRef.current;
    previousViewRef.current = state?.view ?? null;
    if (state?.view !== "overview" || previousView !== "step") return;
    if (state.updatedIndex !== null) showToast(`${nameOf(state.updatedIndex)} saved`, "success");
    void overview.refresh();
  }, [state, nameOf, overview, showToast]);

  // While a combined run's form is open, load its booking's data, so Next opens the booking at once.
  const hotelBookingLinkId = hotelStep?.kind === "hotel-booking" ? hotelStep.bookingLinkId : null;
  useEffect(() => {
    if (!isCombinedRun || hotelBookingLinkId === null) return;
    getCachedHotelBookingEmbedBundle(display.eventId, hotelBookingLinkId).catch(() => {
      // Only a warm-up: the booking step loads (and reports errors) on its own.
    });
  }, [isCombinedRun, hotelBookingLinkId, display.eventId]);

  // A closed dialog drops the combined run's handoff, so reopening starts the form fresh.
  useEffect(() => {
    if (state) return;
    handoffRef.current = null;
    setHandedOverForm(null);
  }, [state]);

  // Render
  if (!state) return null;

  const fromOverview = state.view === "step" && state.origin === "overview";
  const position = stepIndex === null ? -1 : openableIndexes.indexOf(stepIndex);
  const previousIndex = fromOverview && position > 0 ? openableIndexes[position - 1] : null;
  const nextIndex =
    fromOverview && position !== -1 && position < openableIndexes.length - 1 ? openableIndexes[position + 1] : null;

  // A combined run keeps the form's name as the title for every step.
  const title = stepIndex === null ? display.title : nameOf(isCombinedRun ? 0 : stepIndex);
  const subtitle = fromOverview && stepIndex !== null ? `Step ${stepIndex + 1} of ${steps.length}` : undefined;

  // Wide only while a step's form is open for input; recaps, summaries, and the overview stay small.
  const isWide = state.view === "step" && guard.isEditing;

  const bottomBar =
    previousIndex !== null || nextIndex !== null ? (
      <nav aria-label="Steps" className="flex items-center justify-between gap-2">
        {previousIndex !== null ? (
          <FetchButton
            variant="ghost"
            leftIcon={<ChevronLeft className="h-4 w-4" />}
            onClick={() => handleGoTo(previousIndex)}
          >
            <span className="block max-w-40 truncate sm:max-w-56">{nameOf(previousIndex)}</span>
          </FetchButton>
        ) : (
          <span />
        )}
        {nextIndex !== null && (
          <FetchButton
            variant="ghost"
            rightIcon={<ChevronRight className="h-4 w-4" />}
            onClick={() => handleGoTo(nextIndex)}
          >
            <span className="block max-w-40 truncate sm:max-w-56">{nameOf(nextIndex)}</span>
          </FetchButton>
        )}
      </nav>
    ) : undefined;

  return (
    <FetchModal
      isOpen
      onClose={handleClose}
      ariaLabel={subtitle ? `${title}, ${subtitle.toLowerCase()}` : title}
      floatingClose
      bottomBar={bottomBar}
      size="auto"
      className={isWide ? PUBLIC_MODAL_FULL_SCREEN_MOBILE_CLASSNAME : PUBLIC_MODAL_COMPACT_CLASSNAME}
      dismissOnBackdrop={isCombinedRun || !guard.isDirty}
    >
      {/* Our own header (FetchModal's puts extra links beside the title, not above it); stays put while
          the body below scrolls. A combined run shows just the form, like a plain form popup. */}
      {!isCombinedRun && (
        <div className="flex shrink-0 flex-col gap-1 px-1 pb-4 pr-10">
          {fromOverview && (
            <button type="button" onClick={handleBackToOverview} className={BACK_LINK_CLASS}>
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              All steps
            </button>
          )}
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          {subtitle && <p className="text-sm font-medium text-gray-500">{subtitle}</p>}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-1">
        {guard.isAsking && (
          <div className="mb-4">
            <FetchCallout variant="warning">
              <div className="flex flex-col gap-3">
                <p className="text-sm font-medium">Discard your changes?</p>
                <div className="flex flex-wrap gap-2">
                  <FetchButton variant="primary" onClick={guard.keepEditing}>
                    Keep editing
                  </FetchButton>
                  <FetchButton variant="outline" onClick={guard.discard}>
                    Discard
                  </FetchButton>
                </div>
              </div>
            </FetchCallout>
          </div>
        )}

        {state.view === "loading" && (
          <div aria-busy="true" aria-label="Loading your steps" className="flex flex-col gap-2">
            {steps.map((_, index) => (
              <div key={index} className="h-14 animate-pulse rounded-lg bg-gray-100" />
            ))}
          </div>
        )}

        {state.view === "error" && (
          <div role="alert" className="flex flex-col items-start gap-3 py-4">
            <p className="text-sm font-medium text-gray-700">We couldn&apos;t load your steps.</p>
            <div className="flex flex-wrap gap-2">
              <FetchButton variant="primary" onClick={overview.retry}>
                Retry
              </FetchButton>
              <FetchButton variant="outline" onClick={handleStartRun}>
                Start from step 1
              </FetchButton>
            </div>
          </div>
        )}

        {state.view === "overview" && overview.data && (
          <CtaStepsOverviewPanel
            overview={overview.data}
            allSet={state.allSet}
            onOpenStep={goTo}
            onDone={close}
            roomActions={roomActions}
          />
        )}

        {isCombinedRun && formRunSequence && steps[0]?.kind === "form" && (
          <div onChangeCapture={guard.markInput}>
            <div hidden={stepIndex !== 0}>
              <FormModal
                key="combined-form"
                isOpen
                embedded
                onClose={close}
                formId={steps[0].formId}
                eventId={display.eventId}
                primaryColor={display.primaryColor}
                inviteToken={steps[0].inviteToken}
                sequence={formRunSequence}
              />
            </div>
            {/* The booking mounts once reached and stays, so going Back and on again keeps it. */}
            {hotelRunSequence &&
              hotelStep?.kind === "hotel-booking" &&
              (handedOverForm !== null || stepIndex === hotelIndex) && (
                <div hidden={stepIndex !== hotelIndex}>
                  <LazyHotelBookingModal
                    key="combined-booking"
                    isOpen
                    embedded
                    onClose={close}
                    eventId={display.eventId}
                    bookingLinkId={hotelStep.bookingLinkId}
                    primaryColor={display.primaryColor}
                    sequence={hotelRunSequence}
                  />
                </div>
              )}
          </div>
        )}

        {!isCombinedRun && step && sequence && stepIndex !== null && (
          // A change anywhere in the step marks it dirty while its form is open (see onEditingChange).
          // React's onChange only: it batches with the field's own update, where a raw `input` event
          // re-renders first and resets a controlled <select> before its `change` lands.
          <div onChangeCapture={guard.markInput}>
            {step.kind === "form" ? (
              <FormModal
                key={`step-${stepIndex}`}
                isOpen
                embedded
                onClose={close}
                formId={step.formId}
                eventId={display.eventId}
                primaryColor={display.primaryColor}
                inviteToken={step.inviteToken}
                sequence={sequence}
              />
            ) : (
              <LazyHotelBookingModal
                key={`step-${stepIndex}`}
                isOpen
                embedded
                onClose={close}
                eventId={display.eventId}
                bookingLinkId={step.bookingLinkId}
                primaryColor={display.primaryColor}
                sequence={sequence}
              />
            )}
          </div>
        )}
      </div>
    </FetchModal>
  );
}
